import {ChevronDownIcon, ChevronRightIcon, FoldDownIcon, FoldUpIcon, XIcon} from '@primer/octicons-react'
import {ActionList, Button, IconButton, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useState} from 'react'

import styles from './ReferencePreview.module.css'

function Frame({children}: React.PropsWithChildren) {
  return (
    <div className={styles.frame}>
      <div className={styles.frameInner}>
        <div className={styles.frameContent}>{children}</div>
      </div>
    </div>
  )
}

function Header({children, onDismiss}: React.PropsWithChildren<{onDismiss?: () => void}>) {
  return (
    <h3 className={styles.header}>
      <div className={styles.headerContent}>{children}</div>
      {onDismiss && (
        <IconButton
          aria-label="Dismiss"
          icon={XIcon}
          onClick={onDismiss}
          variant="invisible"
          className={styles.dismissButton}
        />
      )}
    </h3>
  )
}

function SectionDivider({children}: React.PropsWithChildren) {
  return <div className={styles.sectionDivider}>{children}</div>
}

function CollapsibleSubsection({
  children,
  title,
  initiallyOpen,
}: React.PropsWithChildren<{title: string; initiallyOpen?: boolean}>) {
  const [open, setOpen] = useState(initiallyOpen ?? false)
  return (
    <>
      <Button aria-expanded={open} onClick={() => setOpen(wasOpen => !wasOpen)} className={styles.collapsibleButton}>
        {open ? (
          <ChevronDownIcon className={styles.iconMarginRight} />
        ) : (
          <ChevronRightIcon className={styles.iconMarginRight} />
        )}
        {title}
      </Button>
      {open && children}
    </>
  )
}

function Content({children}: React.PropsWithChildren) {
  return <div className={styles.content}>{children}</div>
}

function ContentExpander({direction, onExpand}: {direction: 'above' | 'below'; onExpand: () => void}) {
  return (
    <Button
      onClick={onExpand}
      className={clsx(styles.expanderButton, direction === 'above' ? styles.expanderAbove : styles.expanderBelow)}
    >
      <div className={styles.expanderContent}>
        {direction === 'below' ? (
          <FoldDownIcon className={styles.iconMarginRight} />
        ) : (
          <FoldUpIcon className={styles.iconMarginRight} />
        )}
        Show content {direction}
      </div>
    </Button>
  )
}

function Details({children}: React.PropsWithChildren) {
  return (
    <ActionList>
      <ActionList.Group>
        <ActionList.GroupHeading as="h3">Reference details</ActionList.GroupHeading>
        {children}
      </ActionList.Group>
    </ActionList>
  )
}

function DetailLink({children, href, icon}: React.PropsWithChildren<{href: string; icon: React.ReactElement}>) {
  return (
    <ActionList.LinkItem href={href}>
      <ActionList.LeadingVisual>{icon}</ActionList.LeadingVisual>
      {children}
    </ActionList.LinkItem>
  )
}

function Loading() {
  return (
    <div className={styles.loadingContainer}>
      <Spinner className={styles.loadingSpinner} />
      <div>Loading...</div>
    </div>
  )
}

function Error() {
  return (
    <div className={styles.errorContainer}>
      <div className={styles.errorMessage}>Failed to load reference details</div>
    </div>
  )
}

function Body({
  children,
  detailsError,
  detailsLoading,
}: React.PropsWithChildren<{detailsError: boolean; detailsLoading: boolean}>) {
  return detailsError ? <Error /> : detailsLoading ? <Loading /> : <>{children}</>
}

export const ReferencePreview = {
  Frame,
  Header,
  Body,
  Content,
  CollapsibleSubsection,
  ContentExpander,
  Details,
  DetailLink,
  SectionDivider,
}
